import { CampaignService } from "@/services/network/campaign";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSnackbar } from "notistack";
import { useNavigate } from "react-router-dom";

const campaignService = new CampaignService();
interface CampaignDetail {
  id: number;
  status: string;
  campaignId: string;
  name: string;
  startDate: string;
  dataSource: string;
  bulkUploadHistoryId: number | null;
  repeat: boolean;
  repeatFrequency: string | null;
  repeatEnd: string | null;
  repeatEndNever: boolean;
  repeatEndAfterNumberOfOccurrences: number | null;
  repeatEndOn: string | null;
  endDate: string;
  remarks: string;
  createdBy: number;
  updatedBy: number;
  activatedBy: number | null;
  activatedAt: string | null;
  createdAt: string;
  updatedAt: string;
  createdUser: {
    id: number;
    firstName: string;
    lastName: string;
  };
  uploadFile: {
    id: number;
    name: string;
    type: string;
    size: number;
  };
  campaignChannels: [
    {
      channel: string;
      triggerBy: string;
      condition: string;
      action: string;
      numberOfDayCheck: boolean;
      specificDayCheck: boolean;
      allocationLimit: number | null;
      acr: number | null;
      kmRange: number | null;
      collectorGender: string | null;
      collectorSegment: string | null;
      performanceRating: string | null;
      itinerary: string | null;
      mainOperator: string;
      previousMonthAllocation: boolean;
      collectionSuccess: boolean;
      collectionFailure: boolean;
      bucketWiseLoadBalancing: boolean;
      allocationStatus: string | null;
      campaignDataChannels: {
        id: number;
        sequentialNumber: number;
        day: string[];
        numberOfDays: number | null;
        timeBand: string[];
        templateScriptId: number;
        campaignChannelId: number;
        campaignTemplate: {
          id: number;
          name: string;
        };
      }[];
      portfolioCategory: string | null;
      state: string | null;
      district: string | null;
      area: string | null;
    }
  ];
  loanParams: {
    accountType: string[];
    mob: string;
    bucket: string[];
    portfolio: number[];
    overdueAmountBand: string[];
    valueAtRisks: string[];
    firstEmi: string;
    secondEmi: string;
    thirdEmi: string;
    dealerBranch: string[];
    paymentFrequency: string;
  };
  customerParams: {
    stateIds: number[];
    districtIds: number[];
    areaIds: number[];
    pincodeIds: number[];
    occupation: string[];
    ageBand: string[];
    customerRiskProfile: string[];
    channelPropensity: string[];
    propensityToPay: string[];
    paidLastThreeMonths: boolean;
    onlinePaidEver: boolean;
    currentMonthBounce: false;
  };
}

const useCampaignParams = (type: string) => {
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: [CampaignService.QUERY_KEY.FETCH_ALL, type],
    queryFn: () => campaignService.getCampaignParams(type),
    enabled: false,
  });

  return {
    data,
    isLoading,
    isError,
    refetch,
  };
};

const useCampaignList = (params: any) => {
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: [CampaignService.QUERY_KEY.FETCH_ALL],
    queryFn: () => campaignService.getCampaignList(params),
    enabled: false,
  });

  return {
    data,
    isLoading,
    isError,
    refetch,
  };
};

const useCampaignDetail = (id: string) => {
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: [CampaignService.QUERY_KEY.FETCH_ALL, id],
    queryFn: () => campaignService.getCampaignDetail(id),
    enabled: false,
  });

  return {
    data,
    isLoading,
    isError,
    refetch,
  };
};

const useCampaignCreate = () => {
  const { enqueueSnackbar } = useSnackbar();
  const navigator = useNavigate();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (params: any) => campaignService.createCampaign(params),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [CampaignService.QUERY_KEY.FETCH_ALL],
      });
      navigator("/campaigns");
      enqueueSnackbar("Campaign created successfully", {
        variant: "success",
      });
    },
    onError: (e) => {
      enqueueSnackbar(e?.message || "Failed to create campaign", {
        variant: "error",
      });
    },
  });

  return {
    mutation,
    status: mutation.status,
    isPending: mutation.isPending,
  };
};

const useCampaignUpdate = () => {
  const { enqueueSnackbar } = useSnackbar();
  const navigator = useNavigate();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (params: any) =>
      campaignService.updateCampaign(params.id, params.data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [CampaignService.QUERY_KEY.FETCH_ALL],
      });
      enqueueSnackbar("Campaign updated successfully", {
        variant: "success",
      });
      navigator("/campaigns");
    },
    onError: (e) => {
      enqueueSnackbar(e?.message || "Failed to update campaign", {
        variant: "error",
      });
    },
  });

  return {
    mutation,
    status: mutation.status,
    isPending: mutation.isPending,
  };
};

const useCampaignSwitchStatus = () => {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (params: any) => campaignService.switchStatus(params),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [CampaignService.QUERY_KEY.FETCH_ALL],
      });
    },
  });

  return {
    mutation,
    status: mutation.status,
    isPending: mutation.isPending,
  };
};

const useCampaignDownloadReport = () => {
  const mutation = useMutation({
    mutationFn: (campaignId: string) => campaignService.downloadReport(campaignId),
  });

  return mutation;
};

const useCampaignCopy = () => {
  const mutation = useMutation({
    mutationFn: (id: string | number) => campaignService.copyCampaign(id),
  });

  return {
    mutation,
    status: mutation.status,
    isPending: mutation.isPending,
  };
};

export {
  useCampaignCopy,
  useCampaignCreate,
  useCampaignDetail,
  useCampaignDownloadReport,
  useCampaignList,
  useCampaignParams,
  useCampaignSwitchStatus,
  useCampaignUpdate,
};

export type { CampaignDetail };
